import { normalizeForComparison } from '@hopdrive/sdk-lanes';

/** Leading house number of an address, or null when it has none. */
export const getStreetNumber = (address?: string | null): string | null => {
  const match = (address || ``).trim().match(/^(\d+)(?:-\d+)?\b/);
  return match ? match[1] : null;
};

/** The street portion of an address line without its house number, normalized for
 *  comparison. Uses the SDK's normalizer so "Blvd" and "Boulevard" compare equal, keeping
 *  this consistent with how location resolution compares addresses elsewhere. */
const getStreetPart = (value?: string | null) => {
  const firstSegment = (value || ``).split(`,`)[0]?.trim() || ``;
  const withoutNumber = firstSegment.replace(/^\d+(?:-\d+)?\s+/, ``);
  return withoutNumber ? normalizeForComparison(withoutNumber) : ``;
};

/** True when a location's name leads with a street number that contradicts its address.
 *
 *  Deliberately narrow: it only fires when the name is a restatement of the *same street*
 *  with a different number — "20 Busch Gardens Blvd" against an address of "1 Busch Gardens
 *  Blvd". Real names pass freely, including ones that happen to open with a digit
 *  ("7-Eleven", "24 Hour Towing"), because their street portion won't match the address.
 *  A numberless address has nothing to contradict, so it never warns. */
export const hasNameAddressNumberConflict = (name?: string | null, address?: string | null) => {
  const nameNumber = getStreetNumber(name);
  const addressNumber = getStreetNumber(address);
  if (!nameNumber || !addressNumber || nameNumber === addressNumber) return false;
  const nameStreet = getStreetPart(name);
  return !!nameStreet && nameStreet === getStreetPart(address);
};
